/**
 * Sair da conta com dados offline no aparelho.
 *
 * O problema real: o IndexedDB guarda a viagem inteira e pode guardar um
 * outbox com escritas que ainda não chegaram ao servidor. Sair sem tratar isso
 * tem duas consequências ruins, e a segunda é pior que a primeira:
 *
 *   1. a pessoa perde alterações que acha que registrou;
 *   2. **a próxima conta a entrar neste aparelho vê os dados da anterior** —
 *      e, se o outbox sobrevivesse, escreveria em nome dela ao sincronizar.
 *
 * Por isso a regra é dura: **nunca preservar outbox de um usuário para a
 * sessão de outro.** Ou as pendências vão embora sincronizadas, ou vão embora
 * descartadas por decisão explícita — nunca ficam.
 *
 * A limpeza acontece mesmo se o servidor não responder. Uma sessão que não
 * pôde ser revogada por falta de rede é um problema; dados da conta anterior
 * legíveis no aparelho é outro, e este está sob nosso controle.
 */

export type LogoutOutcome =
  /** Saiu. Nada ficou para trás. */
  | { kind: 'signed_out'; serverSessionRevoked: boolean }
  /**
   * Há trabalho não sincronizado. A UI precisa mostrar quantas alterações são
   * e oferecer "Sincronizar agora" ou "Descartar e sair".
   */
  | { kind: 'blocked_by_pending'; pending: number };

export interface LogoutDeps {
  countPendingMutations: () => Promise<number>;
  /** Revoga a sessão no servidor. Pode falhar offline. */
  revokeServerSession: () => Promise<void>;
  /** Apaga IndexedDB, caches do SW, cache do TanStack e storage da conta. */
  clearLocalData: (options: { force: boolean }) => Promise<void>;
}

export async function performLogout(
  deps: LogoutDeps,
  options: { discardPending?: boolean } = {},
): Promise<LogoutOutcome> {
  const pendentes = await deps.countPendingMutations();

  if (pendentes > 0 && !options.discardPending) {
    return { kind: 'blocked_by_pending', pending: pendentes };
  }

  let revogou = false;
  try {
    await deps.revokeServerSession();
    revogou = true;
  } catch {
    // Sem rede não dá para revogar agora. A limpeza local continua, porque é
    // ela que impede a próxima conta de ler o que era da anterior.
    revogou = false;
  }

  // `force` é seguro aqui: ou não havia pendências, ou a pessoa confirmou o
  // descarte na folha de confirmação.
  await deps.clearLocalData({ force: true });

  return { kind: 'signed_out', serverSessionRevoked: revogou };
}

/**
 * Texto da confirmação de descarte.
 *
 * Fica junto da regra porque o número precisa aparecer: "descartar alterações"
 * é abstrato, "descartar 3 alterações" é uma decisão informada.
 */
export function textoDeDescarte(pending: number): string {
  const plural = pending === 1 ? 'alteração' : 'alterações';
  return `${pending} ${plural} ainda não ${pending === 1 ? 'foi enviada' : 'foram enviadas'} ao servidor. Se sair agora sem sincronizar, ${pending === 1 ? 'ela será perdida' : 'elas serão perdidas'}.`;
}
